import type { CompanyFilterOptions } from '@/components/shared/LoadsTable/CompanyFilterButton'
import { isRecord } from '@/utils/isRecord'

const STORAGE_VERSION = 3
const PREVIOUS_STORAGE_VERSION = 2
const STORAGE_PREFIX = 'boxTruckLoads:companyFilter'
const LEGACY_ANONYMOUS_STORAGE_KEY = `${STORAGE_PREFIX}:anon`
export const BOX_TRUCK_COMPANY_FILTER_TTL_MS = 7 * 24 * 60 * 60 * 1000

interface StoredCompanyFilter {
    version: number
    expiresAt: number
    value: CompanyFilterOptions
}

interface ValidStoredCompanyFilter {
    expiresAt: number
    value: CompanyFilterOptions
}

export const boxTruckCompanyFilterStorageKey = (projectName: string | undefined, userId: string | undefined): string | null =>
    projectName && userId ? `${STORAGE_PREFIX}:v${STORAGE_VERSION}:${encodeURIComponent(projectName)}:${encodeURIComponent(userId)}` : null

const removeBoxTruckCompanyFilter = (storageKey: string | null): void => {
    if (!storageKey) return
    try {
        localStorage.removeItem(storageKey)
    } catch (error) {
        console.warn('[box-truck] Could not remove the company filter.', error)
    }
}

/** Remove formats that cannot safely participate in the scoped tab-list migration. */
export const removeLegacyBoxTruckCompanyFilters = (projectName: string | undefined, userId: string | undefined): void => {
    removeBoxTruckCompanyFilter(LEGACY_ANONYMOUS_STORAGE_KEY)
    if (userId) removeBoxTruckCompanyFilter(`${STORAGE_PREFIX}:${userId}`)
    if (projectName && userId) removeBoxTruckCompanyFilter(`${STORAGE_PREFIX}:v1:${encodeURIComponent(projectName)}:${encodeURIComponent(userId)}`)
}

const previousStorageKeyBelongsToScope = (storageKey: string, projectName: string, userId: string): boolean => {
    const parts = storageKey.split(':')
    return (
        parts.length === 6 &&
        parts[0] === 'boxTruckLoads' &&
        parts[1] === 'companyFilter' &&
        parts[2] === `v${PREVIOUS_STORAGE_VERSION}` &&
        parts[3] === encodeURIComponent(projectName) &&
        parts[4] === encodeURIComponent(userId)
    )
}

const parseCompanyFilter = (value: unknown): CompanyFilterOptions | null => {
    if (!isRecord(value)) return null

    return {
        mode: value.mode === 'exclude' ? 'exclude' : 'show',
        companyNames: Array.isArray(value.companyNames) ? value.companyNames.filter((name): name is string => typeof name === 'string') : [],
        listType: value.listType === 'preferred' || value.listType === 'blocked' ? value.listType : 'all',
    }
}

const readStoredCompanyFilter = (storageKey: string, version: number, now: number): ValidStoredCompanyFilter | null => {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null

    try {
        const stored: unknown = JSON.parse(raw)
        if (
            !isRecord(stored) ||
            stored.version !== version ||
            typeof stored.expiresAt !== 'number' ||
            !Number.isFinite(stored.expiresAt) ||
            stored.expiresAt <= now
        ) {
            localStorage.removeItem(storageKey)
            return null
        }

        const value = parseCompanyFilter(stored.value)
        if (!value) localStorage.removeItem(storageKey)
        return value ? { expiresAt: stored.expiresAt, value } : null
    } catch (error) {
        console.warn('[box-truck] Could not restore the company filter.', error)
        localStorage.removeItem(storageKey)
        return null
    }
}

export const readBoxTruckCompanyFilter = (storageKey: string | null, now = Date.now()): CompanyFilterOptions | null => {
    if (!storageKey) return null

    try {
        return readStoredCompanyFilter(storageKey, STORAGE_VERSION, now)?.value ?? null
    } catch (error) {
        console.warn('[box-truck] Could not restore the company filter.', error)
        return null
    }
}

const writeStoredCompanyFilter = (storageKey: string, value: CompanyFilterOptions, expiresAt: number): boolean => {
    const stored: StoredCompanyFilter = { version: STORAGE_VERSION, expiresAt, value }
    try {
        localStorage.setItem(storageKey, JSON.stringify(stored))
        return true
    } catch (error) {
        console.warn('[box-truck] Could not persist the company filter.', error)
        return false
    }
}

export const writeBoxTruckCompanyFilter = (storageKey: string | null, value: CompanyFilterOptions, now = Date.now()): void => {
    if (storageKey) writeStoredCompanyFilter(storageKey, value, now + BOX_TRUCK_COMPANY_FILTER_TTL_MS)
}

/**
 * Consolidate actual exclusion lists from old tab-scoped records into one v3 record for this user
 * and company. Show-only/preferred views are discarded instead of being promoted to every tab.
 */
export const migrateBoxTruckCompanyFilter = (projectName: string | undefined, userId: string | undefined, now = Date.now()): CompanyFilterOptions | null => {
    const nextKey = boxTruckCompanyFilterStorageKey(projectName, userId)
    if (!projectName || !userId || !nextKey) return null

    const current = readBoxTruckCompanyFilter(nextKey, now)
    if (current) return current

    try {
        const previousKeys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
            (key): key is string => typeof key === 'string' && previousStorageKeyBelongsToScope(key, projectName, userId)
        )
        const previous = previousKeys
            .map((key) => ({ key, stored: readStoredCompanyFilter(key, PREVIOUS_STORAGE_VERSION, now) }))
            .filter((entry): entry is { key: string; stored: ValidStoredCompanyFilter } => entry.stored !== null)
        if (previous.length === 0) return null

        const exclusionLists = previous.filter(
            ({ stored }) => stored.value.mode === 'exclude' && stored.value.listType === 'all' && stored.value.companyNames.length > 0
        )
        if (exclusionLists.length === 0) {
            previousKeys.forEach((key) => localStorage.removeItem(key))
            return null
        }

        const migratedValue: CompanyFilterOptions = {
            mode: 'exclude',
            companyNames: [...new Set(exclusionLists.flatMap(({ stored }) => stored.value.companyNames))],
            listType: 'all',
        }
        const expiresAt = Math.max(...exclusionLists.map(({ stored }) => stored.expiresAt))

        if (!writeStoredCompanyFilter(nextKey, migratedValue, expiresAt)) return migratedValue
        previousKeys.forEach((key) => localStorage.removeItem(key))
        return migratedValue
    } catch (error) {
        console.warn('[box-truck] Could not migrate the company filter.', error)
        return null
    }
}
